import { useEffect, useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { getPatBandDistribution } from '../../../../services/analytics/analytics.service'
import Loading from '../../../Loading/Loading'
import DataAsOf from './DataAsOf'

// Paleta Maestro (consistente con NaplanECharts)
const C_BAR = '#2b5784'
const C_BAR_SOFT = '#90bcd8'

const SCALES = [
  { value: '', label: 'All' },
  { value: 'pat-maths-scale', label: 'PAT Maths' },
  { value: 'pat-reading-scale', label: 'PAT Reading' }
]

// Ordena las bandas por su cota inferior (parseInt: "95-104"→95, "64 and below"→64,
// "155 and above"→155). ACER las muestra de mayor (arriba) a menor.
const bandLower = (b) => {
  const n = parseInt(String(b).replace(/[^\d-]/g, ''), 10)
  return Number.isNaN(n) ? -1 : n
}

const Toggle = ({ value, onChange }) => (
  <div style={{ display: 'flex', gap: 6 }}>
    {SCALES.map((s) => (
      <button
        key={s.value}
        onClick={() => onChange(s.value)}
        style={{
          fontSize: 12,
          fontWeight: 600,
          padding: '5px 11px',
          borderRadius: 7,
          cursor: 'pointer',
          border: '1px solid ' + (value === s.value ? C_BAR : '#d5dbe4'),
          background: value === s.value ? C_BAR : '#fff',
          color: value === s.value ? '#fff' : '#5a6472'
        }}
      >
        {s.label}
      </button>
    ))}
  </div>
)

const buildOption = (rows) => {
  const sorted = [...rows].sort((a, b) => bandLower(a.band_key) - bandLower(b.band_key))
  const total = sorted.reduce((acc, r) => acc + Number(r.students || 0), 0) || 1
  const bands = sorted.map((r) => r.band_key)
  const pct = sorted.map((r) => +((Number(r.students || 0) / total) * 100).toFixed(1))
  const counts = sorted.map((r) => Number(r.students || 0))

  return {
    backgroundColor: 'transparent',
    grid: { left: 92, right: 48, top: 10, bottom: 30 },
    xAxis: {
      type: 'value',
      name: 'Student %',
      nameLocation: 'middle',
      nameGap: 26,
      nameTextStyle: { fontSize: 11, color: '#888' },
      axisLabel: { fontSize: 10, color: '#aaa', formatter: '{value}%' },
      splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
    },
    yAxis: {
      type: 'category',
      data: bands,
      axisLabel: { fontSize: 11, color: '#555' },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#e0e0e0' } }
    },
    series: [
      {
        type: 'bar',
        barWidth: '62%',
        data: pct.map((v, i) => ({
          value: v,
          count: counts[i],
          itemStyle: { color: v > 0 ? C_BAR : C_BAR_SOFT, borderRadius: [0, 3, 3, 0] }
        })),
        label: {
          show: true,
          position: 'right',
          fontSize: 10,
          color: '#333',
          formatter: (p) => (p.value > 0 ? p.value + '%' : '')
        }
      }
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const p = params[0]
        return `<b>${p.name}</b><br/>${p.value}% · ${p.data.count} students`
      }
    }
  }
}

const PatAchievementBands = () => {
  const [scale, setScale] = useState('')
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    setLoading(true)
    getPatBandDistribution(scale ? { scaleSlug: scale } : {})
      .then((res) => setData(res?.data || []))
      .catch((err) => setError(err?.message || 'Error loading PAT band distribution'))
      .finally(() => setLoading(false))
  }, [scale])

  const option = useMemo(() => buildOption(data), [data])

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 16,
          flexWrap: 'wrap'
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: C_BAR }}>
            Achievement band distribution
          </h2>
          <DataAsOf />
        </div>
        <Toggle value={scale} onChange={setScale} />
      </div>

      {loading ? (
        <Loading />
      ) : error ? (
        <div style={{ padding: 24, color: '#c0392b' }}>
          <strong>Error:</strong> {error}
        </div>
      ) : !data.length ? (
        <p style={{ color: '#888', padding: 24 }}>No PAT data yet — run a sync and ingest first.</p>
      ) : (
        <ReactECharts
          option={option}
          style={{ height: Math.max(320, data.length * 34), width: '100%' }}
          opts={{ renderer: 'canvas' }}
          notMerge
        />
      )}
    </div>
  )
}

export default PatAchievementBands
